import { ContractSuretyType, Dtos, type NaicsCodeId, SuretyType } from "@inrev/common";
import { BondRequest, useUpdateBondRequest } from "@portal/api/bond-request/agent";
import { cn } from "@portal/components/lib/utils";
import { createFormResolver } from "@portal/components/multiform";
import { Button, Card, ClickableIcon } from "@portal/components/primitives";
import { useIsMinimalBondUIEnabled } from "@portal/hooks/useIsMinimalBondUIEnabled";
import { useVersionConflictSync } from "@portal/hooks/useVersionConflictSync";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { match } from "ts-pattern";
import { z } from "zod";
import {
	getFormCompletion,
	getNestedValue,
	getPartialFieldPaths,
	getRequiredForApprovalFieldPaths,
	hasOwnPath,
	isEmpty,
} from "../../../utils/schema-fields";
import { AccountPartialSubmitConfirmationModal } from "../../shared/AccountPartialSubmitConfirmationModal";
import {
	VIEW_SECTION_STICKY_HEADER_CLASS,
	ViewSectionLayout,
} from "../../shared/layouts/ViewSectionLayout";
import {
	BondAmountCurrencyPercentInput,
	BondAmountInput,
	type BondRequestRowProps,
	ScopeOfWorkInput,
} from "../../shared/request/BondRequestDetailRows";
import { VIEW_SECTION_CARD_STACK_CLASS } from "../../shared/ViewSectionCardEditItem";

type AgentBondRequestDetailsCardProps = {
	request: BondRequest;
	accountNaicsCodes: NaicsCodeId[];
};

type ContractUpdateForm = Dtos.SuretyQuote.Update.Request;
const ContractUpdateForm = Dtos.SuretyQuote.Update.Request;
const BidUpdateForm = Dtos.SuretyQuote.Update.Bid;
const FinalUpdateForm = Dtos.SuretyQuote.Update.Final;
const BidToFinalUpdateForm = Dtos.SuretyQuote.Update.BidToFinal;

const EMPTY_NAICS_CODE_IDS: NaicsCodeId[] = [];

const BID_BOND_CURRENCY_PERCENT_NAMES = {
	referenceSourceName: "bidAmount",
	currencyName: "bondAmount",
	percentName: "bondAmountPercentOfBid",
	amountTypeName: "bidBondAmountType",
} as const;

const toUpdateForm = (request: BondRequest) => {
	const parsed = ContractUpdateForm.schema.safeParse({
		...request,
		suretyType: SuretyType.contract,
		project: {
			...request.project,
			scopeOfWork: (request.project.scopeOfWork ?? []).map((row) => row.id),
			city: request.project.address?.city,
			state: request.project.address?.state,
		},
	});
	if (!parsed.success) throw new Error(z.prettifyError(parsed.error));
	return parsed.data;
};

export const AgentBondRequestDetailsCard = ({
	request,
	accountNaicsCodes,
}: AgentBondRequestDetailsCardProps) => {
	const [editing, setEditing] = useState(false);
	const isMinimalUIEnabled = useIsMinimalBondUIEnabled();
	const requestRef = useRef(request);
	requestRef.current = request;
	const pendingSubmitDataRef = useRef<ContractUpdateForm | null>(null);
	const [partialSubmit, setPartialSubmit] = useState<{ percentComplete?: number } | null>(null);
	const isEditable =
		request.status !== "pending" && request.status !== "declined" && request.status !== "closed";
	const scopeOfWorkIds = useMemo(
		() => request.project?.scopeOfWork?.map((row) => row.id) ?? EMPTY_NAICS_CODE_IDS,
		[request.project?.scopeOfWork],
	);
	const schema = match(request.contractSuretyType)
		.with(ContractSuretyType.bid, () => BidUpdateForm)
		.with(ContractSuretyType.final, () => FinalUpdateForm)
		.with(ContractSuretyType.bid_to_final, () => BidToFinalUpdateForm)
		.exhaustive();
	const [initialValues] = useState(() => toUpdateForm(request));
	const formMethods = useForm<ContractUpdateForm>({
		defaultValues: initialValues,
		mode: "onBlur",
		reValidateMode: "onBlur",
		resolver: createFormResolver<ContractUpdateForm>(ContractUpdateForm.schema),
	});
	const { formState, handleSubmit } = formMethods;
	const { updateBondRequest, updateBondRequestIsLoading } = useUpdateBondRequest(
		request.id,
		request.suretyAccountId,
		{
			onConflict: () => {
				formMethods.reset(toUpdateForm(requestRef.current));
				setEditing(false);
			},
		},
	);

	useVersionConflictSync(request.updatedAt, () => {
		if (!editing) formMethods.reset(toUpdateForm(request));
	});

	const submitUpdate = (data: ContractUpdateForm) => {
		updateBondRequest({
			data,
			onSuccess: () => {
				setEditing(false);
			},
		});
	};
	const onSubmit = (data: ContractUpdateForm) => {
		const emptyPaths = [
			...getPartialFieldPaths(schema, data),
			...getRequiredForApprovalFieldPaths(schema, data),
		].filter((path) => hasOwnPath(data, path) && isEmpty(getNestedValue(data, path)));

		if (emptyPaths.length > 0) {
			pendingSubmitDataRef.current = data;
			setPartialSubmit({ percentComplete: getFormCompletion(schema, data) });
			return;
		}
		submitUpdate(data);
	};
	const handleCancel = useCallback(() => {
		formMethods.reset(toUpdateForm(request));
		setEditing(false);
	}, [formMethods, request]);

	useEffect(() => {
		if (!editing || updateBondRequestIsLoading) return;
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") handleCancel();
		};
		window.addEventListener("keydown", onKeyDown);
		return () => window.removeEventListener("keydown", onKeyDown);
	}, [editing, updateBondRequestIsLoading, handleCancel]);

	const componentProps: Omit<BondRequestRowProps<ContractUpdateForm>, "name"> = {
		formMethods,
		editing,
		schema,
		partialRequiresSubmit: false,
		isMinimalUIEnabled,
	};

	return (
		<Card
			elevation="raised"
			className="rounded-xl"
			id="bond-details-section"
			data-testid="agent-bond-request-bond-details-card"
		>
			<Card.Body className="p-6">
				<FormProvider {...formMethods}>
					<ViewSectionLayout
						header="Bond Details"
						partial={false}
						partialActive={false}
						headerClassName={editing ? VIEW_SECTION_STICKY_HEADER_CLASS : undefined}
						button={
							isEditable ? (
								editing ? (
									<div className="flex space-x-[10px]">
										<Button
											variant="outline"
											color="secondary"
											size="sm"
											disabled={updateBondRequestIsLoading}
											onClick={handleCancel}
										>
											Cancel
										</Button>
										<Button
											variant="solid"
											color="primary"
											size="sm"
											disabled={!formState.isDirty}
											loading={updateBondRequestIsLoading}
											onClick={handleSubmit(onSubmit)}
										>
											Save
										</Button>
									</div>
								) : (
									<ClickableIcon
										type="pencil-square"
										size="mini"
										color="secondary"
										aria-label="Edit bond details"
										data-testid="agent-bond-request-bond-details-edit"
										trackingId="bond-request-agent-bond-details-edit"
										onClick={() => setEditing(true)}
									/>
								)
							) : undefined
						}
					>
						<div
							className={cn(
								"w-full h-full bg-surface-base rounded-md",
								VIEW_SECTION_CARD_STACK_CLASS,
							)}
						>
							{request.contractSuretyType === ContractSuretyType.bid ? (
								<BondAmountCurrencyPercentInput
									{...componentProps}
									bondAmount={request.bondAmount}
									currencyPercentNames={BID_BOND_CURRENCY_PERCENT_NAMES}
									name="bondAmount"
								/>
							) : (
								<BondAmountInput
									{...componentProps}
									bondAmount={request.bondAmount}
									name="bondAmount"
								/>
							)}
							<ScopeOfWorkInput
								{...componentProps}
								scopeOfWork={scopeOfWorkIds}
								accountNaicsCodes={accountNaicsCodes}
								name="project.scopeOfWork"
							/>
						</div>
						{partialSubmit && (
							<AccountPartialSubmitConfirmationModal
								context="view"
								userContext="agent"
								percentComplete={partialSubmit.percentComplete}
								onClose={() => setPartialSubmit(null)}
								onSubmit={() => {
									const pendingData = pendingSubmitDataRef.current;
									pendingSubmitDataRef.current = null;
									setPartialSubmit(null);
									if (pendingData) submitUpdate(pendingData);
								}}
							/>
						)}
					</ViewSectionLayout>
				</FormProvider>
			</Card.Body>
		</Card>
	);
};
